/**
 * EditMode + EditableText cho Safe Self-Host.
 * Port nguyên trạng từ src/lib/edit-mode.tsx + src/lib/site-texts.tsx (Phase 7)
 * nhưng:
 *  - Bỏ phụ thuộc Supabase (dùng src/lib/api.ts PHP).
 *  - Bỏ phụ thuộc sonner toast (dùng alert nhẹ + console).
 *  - Tự chứa Provider + hook, không đụng vào file gốc.
 *
 * CSS body.edit-mode-on đã có sẵn trong src/styles.css (Phase 7).
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo,
  useRef, useState,
} from "react";
import { api } from "@/lib/api";

type TextMap = Record<string, string>;
const STORAGE_KEY = "nana.editMode";
const TEXTS_CACHE_KEY = "nana.siteTexts.cache";

function readTextsCache(): TextMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(TEXTS_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as TextMap) : {};
  } catch { return {}; }
}
function writeTextsCache(map: TextMap) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(TEXTS_CACHE_KEY, JSON.stringify(map)); } catch {}
}

interface EditCtx {
  editMode: boolean;
  isAdmin: boolean;
  toggle: () => void;
  setEditMode: (v: boolean) => void;
  texts: TextMap;
  setLocal: (k: string, v: string) => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<EditCtx>({
  editMode: false, isAdmin: false, toggle: () => {}, setEditMode: () => {},
  texts: {}, setLocal: () => {}, refresh: async () => {},
});

export function EditModeProvider({
  isAdmin, children,
}: { isAdmin: boolean; children: React.ReactNode }) {
  const [editMode, setEditModeState] = useState(false);
  // Khởi tạo đồng bộ từ cache localStorage → tránh flash text cũ khi F5.
  const [texts, setTexts] = useState<TextMap>(() => readTextsCache());

  // Restore khi biết là admin
  useEffect(() => {
    if (!isAdmin) { setEditModeState(false); return; }
    if (typeof window !== "undefined") {
      setEditModeState(window.localStorage.getItem(STORAGE_KEY) === "1");
    }
  }, [isAdmin]);

  const setEditMode = useCallback((v: boolean) => {
    setEditModeState(v);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, v ? "1" : "0");
    }
  }, []);

  const active = isAdmin && editMode;

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("edit-mode-on", active);
    return () => { document.body.classList.remove("edit-mode-on"); };
  }, [active]);

  const refresh = useCallback(async () => {
    try {
      const rows = await api.get<Array<{ key: string; value: string }>>("/site-texts");
      const map: TextMap = {};
      for (const r of rows) map[r.key] = r.value;
      setTexts(map);
      writeTextsCache(map);
    } catch (e) { console.warn("[site-texts] load failed:", e); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const setLocal = useCallback((k: string, v: string) => {
    setTexts((prev) => {
      const next = { ...prev, [k]: v };
      writeTextsCache(next);
      return next;
    });
  }, []);

  const value = useMemo<EditCtx>(() => ({
    editMode: active, isAdmin,
    toggle: () => setEditMode(!editMode),
    setEditMode, texts, setLocal, refresh,
  }), [active, isAdmin, editMode, setEditMode, texts, setLocal, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useEditMode() { return useContext(Ctx); }

export function useSiteText(key: string, fallback: string): string {
  const { texts } = useContext(Ctx);
  return texts[key] ?? fallback;
}

interface EditableTextProps {
  textKey: string;
  defaultValue: string;
  as?: keyof React.JSX.IntrinsicElements;
  className?: string;
  multiline?: boolean;
}

export function EditableText({
  textKey, defaultValue, as = "span", className, multiline = false,
}: EditableTextProps) {
  const { editMode, texts, setLocal } = useContext(Ctx);
  const ref = useRef<HTMLElement>(null);
  const [saving, setSaving] = useState(false);
  const value = texts[textKey] ?? defaultValue;
  const Tag = as as any;

  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      ref.current.textContent = value;
    }
  }, [value]);

  if (!editMode) return <Tag className={className}>{value}</Tag>;

  async function save() {
    const newVal = ref.current?.textContent ?? "";
    if (newVal === value) return;
    setSaving(true);
    try {
      await api.put(`/site-texts/${encodeURIComponent(textKey)}`, { value: newVal, upsert: true });
      setLocal(textKey, newVal);
    } catch (e: any) {
      console.error("[site-texts save]", e);
      alert("Lỗi lưu: " + (e?.message ?? "unknown"));
      if (ref.current) ref.current.textContent = value;
    } finally { setSaving(false); }
  }

  return (
    <Tag
      ref={ref as any}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
      onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); }}
      onBlur={save}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (!multiline && e.key === "Enter") { e.preventDefault(); (e.target as HTMLElement).blur(); }
        if (e.key === "Escape") {
          if (ref.current) ref.current.textContent = value;
          (e.target as HTMLElement).blur();
        }
      }}
      data-editable
      title="Click để sửa (Esc huỷ, Enter lưu)"
      className={
        (className ?? "") +
        " outline-none rounded-sm transition-colors " +
        "hover:bg-primary/10 focus:bg-primary/15 focus:ring-2 focus:ring-primary/60 " +
        "ring-offset-2 ring-offset-background cursor-text " +
        (saving ? " opacity-60" : "")
      }
    >
      {value}
    </Tag>
  );
}

/** Nút toggle Edit Mode — bỏ vào header admin. */
export function EditModeToggle() {
  const { editMode, isAdmin, toggle } = useEditMode();
  if (!isAdmin) return null;
  return (
    <button
      type="button"
      onClick={toggle}
      data-edit-keep
      className={
        "rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors " +
        (editMode
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card hover:bg-muted")
      }
      title={editMode ? "Tắt chế độ chỉnh sửa" : "Bật chế độ chỉnh sửa nội dung"}
    >
      {editMode ? "Đang sửa nội dung" : "Sửa nội dung"}
    </button>
  );
}
